import {type Command, Editor, Extension} from "@tiptap/core"
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erTall} from "felles-ng2/støttefunksjoner/tall";
import {dekodTallMedBenevnelse, erPåStartAvNode} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/tiptapHjelpere";
import {NodeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";

/** Tiptap utvidelse som legger til attributtet 'indentering' for å angi innrykk på
 * noder av typen 'paragraph' og 'heading'.
 *
 * Inspirert av https://github.com/ueberdosis/tiptap/issues/1036 */
export const Indentering = Extension.create({
    name: 'indentering',

    addGlobalAttributes() {
        return [{
            types: tillatteNodetyper(),
            attributes: {
                indentering: {
                    default: 0,
                    renderHTML: attributter => indenteringSomHtmlStyleAttributt(attributter, indenteringBreddePx),
                    parseHTML: element => marginLeftSomIndenteringAttributt(element)
                }
            }
        }];
    },

    addCommands(): IndenteringKommandoer<Command> {
        return {
            indent: () => ({editor, chain}) => {
                // dersom liste øker vi liste-sub-nivå
                if (erListenodeAktiv(editor)) return chain().sinkListItem('listItem').run();

                //ellers oppdaterer vi indentering attributtet
                return tillatteNodetyper()
                  .reduce((chain, type) =>
                    chain.updateAttributes(type, {indentering: lesIndenteringAttributtNormalisert(editor, type) + 1}), chain())
                  .run();
            },

            outdent: () => ({editor, chain}) => {
                // dersom liste reduserer vi liste-sub-nivå
                if (erListenodeAktiv(editor)) return chain().liftListItem('listItem').run();

                //ellers oppdaterer vi indentering attributtet
                return tillatteNodetyper()
                  .filter(type => lesIndenteringAttributt(editor, type) > 0)
                  .reduce((chain, type) =>
                    chain.updateAttributes(type, {indentering: lesIndenteringAttributtNormalisert(editor, type) - 1}), chain())
                  .run();
            }
        };
    },

    addKeyboardShortcuts() {
        return {
            Tab: ({editor}) => {
                return editor.commands.indent();
            },

            'Shift-Tab': ({editor}) => {
                return editor.commands.outdent();
            },

            Backspace: ({editor}) => {
                //ignorer dersom vi ikke er på starten av en node
                if (!erPåStartAvNode(editor.state.selection)) return false;
                //ignorer dersom vi ikke har noen indentering å redusere
                if (tillatteNodetyper().every(type => lesIndenteringAttributt(editor, type) == 0)) return false;

                return editor.commands.outdent();
            }
        };
    }
});

const indenteringBreddePx = 40;

function tillatteNodetyper() {
    return [NodeType.Paragraph, NodeType.Heading];
}

function indenteringSomHtmlStyleAttributt(attributter: Record<string, any>, margin: number) {
    return erIkkeNil(attributter.indentering) && attributter.indentering > 0 ?
      {style: `margin-left: ${attributter.indentering * margin}px;`} :
      {};
}

/** Beregner indenteringsnivå for angitt element.
 * Merk at dersom marginLeft er satt til ikke-standard indenteringsverdi (forskjellig fra et helt produkt av indenteringBreddePx) returneres en desimalverdi. */
function marginLeftSomIndenteringAttributt(element: HTMLElement) {
    let eksisterendeMargin = dekodTallMedBenevnelse(element.style.marginLeft, 'px', 0);
    return Math.abs(eksisterendeMargin / indenteringBreddePx);
}

function erListenodeAktiv(editor: Editor): boolean {
    return editor.isActive('listItem') ||
      editor.isActive('bulletList') ||
      editor.isActive('orderedList')
}

/** Returnerer indenteringsnivå for aktiv node. */
function lesIndenteringAttributt(editor: Editor, type: string): number {
    let indentering = editor.getAttributes(type).indentering;
    return erTall(indentering) ? indentering : 0;
}

/** Returnerer indenteringsnivå for aktiv node, normalisert til et helttall. */
function lesIndenteringAttributtNormalisert(editor: Editor, type: string): number {
    //merk at vi her endrer event eksisterende indentering til et helt produkt av indenteringBreddePx
    //dersom noden feks hadde en marking left på 99px vil vi her returnere 99/40 = 2.47 rundet ned til 2
    return Math.round(lesIndenteringAttributt(editor, type));
}


// typedefinisjoner

interface IndenteringKommandoer<ReturnType> {
    /** Øker indentering */
    indent: () => ReturnType;

    /** Reduserer indentering */
    outdent: () => ReturnType;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        indentering: IndenteringKommandoer<ReturnType>
    }
}
